/**
 * AddSingleContactDialog
 * ----------------------
 * Single-contact create form for a hospital, surfaced from the hospital
 * detail dashboard. Mirrors the fields admins already see when editing a
 * contact (Department category + Title are first-class), and hits the
 * existing `POST /health-systems/{hs_id}/contacts` endpoint.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Loader2, UserPlus } from "lucide-react";
import DepartmentSelect from "@/components/admin/DepartmentSelect";

const API = process.env.REACT_APP_BACKEND_URL;

const PORTAL_ROLES = [
  { value: "staff",            label: "Staff" },
  { value: "manager",          label: "Manager" },
  { value: "authorized_agent", label: "Authorized Agent" },
  { value: "director",         label: "Director" },
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  mobile: "",
  title: "",
  department: "Supply Chain",
  facility_id: "",
  role: "staff",
  linkedin: "",
  is_primary: false,
  notes: "",
};

export default function AddSingleContactDialog({
  open, onOpenChange, hospital, token, onSaved,
}) {
  const hospitalId = hospital?.id;
  const facilities = hospital?.facilities || [];
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const submit = async () => {
    if (!form.email?.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name?.trim() || null,
        email: form.email.trim().toLowerCase(),
        phone: form.phone || null,
        mobile: form.mobile || null,
        title: form.title || null,
        department: form.department || null,
        facility_id: form.facility_id || null,
        role: form.role || null,
        linkedin: form.linkedin || null,
        is_primary: !!form.is_primary,
        notes: form.notes || null,
      };
      const res = await axios.post(
        `${API}/api/health-systems/${hospitalId}/contacts`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const created = res.data?.contact;
      toast.success(
        `Added ${created?.name || payload.email} → ${hospital?.name || "hospital"}`
      );
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = "Create failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg || "").join("; ");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="add-single-contact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            Add contact{hospital?.name ? ` → ${hospital.name}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Identity */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Identity</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Name <span className="text-slate-400 font-normal">(optional — defaults to email username)</span></Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Smith"
                  data-testid="add-contact-name"
                />
              </div>
              <div>
                <Label className="text-xs">Title / position</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Director of Supply Chain"
                  data-testid="add-contact-title"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@hospital.org"
                data-testid="add-contact-email"
              />
            </div>
          </section>

          {/* Phones */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Phone / social</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Office phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(281) 555-0123"
                />
              </div>
              <div>
                <Label className="text-xs">Mobile</Label>
                <Input
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="(281) 555-9876"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">LinkedIn</Label>
              <Input
                value={form.linkedin}
                onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                placeholder="https://linkedin.com/in/..."
              />
            </div>
          </section>

          {/* Within the hospital */}
          <section className="space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Within the hospital</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Department category *</Label>
                <DepartmentSelect
                  value={form.department}
                  onChange={(v) => setForm({ ...form, department: v })}
                  testId="add-contact-department"
                />
              </div>
              <div>
                <Label className="text-xs">Facility</Label>
                <Select
                  value={form.facility_id || "_none_"}
                  onValueChange={(v) => setForm({ ...form, facility_id: v === "_none_" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="System-wide" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">System-wide</SelectItem>
                    {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Portal role (used if invited to portal later)</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="add-contact-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PORTAL_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm pt-1">
              <input
                type="checkbox"
                checked={!!form.is_primary}
                onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                className="accent-emerald-600"
                data-testid="add-contact-primary"
              />
              Primary contact for this hospital
            </label>
          </section>

          {/* Notes */}
          <section>
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Internal notes about this contact (only visible to admins)"
            />
          </section>
        </div>

        <DialogFooter className="flex justify-end gap-2 border-t pt-3 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !form.email}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5"
            data-testid="add-contact-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Add contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
